<div
  role="region"
  [attr.aria-label]="'readspeaker.read_aloud' | translate"
  class="audio-controls"
  cdkTrapFocus
  [cdkTrapFocusAutoCapture]="true"
  rdrxHideOnEscape
>
  <div #controlRow class="control-row">
    <div class="audio-controls-container">
    <ng-container *ngIf="(hasError$ | async) === false; else audioErrorMessage">
      <button
        #playPauseButton
        mhe-button
        [purpose]="ButtonPurpose.Icon"
        class="tts-button mhe-a11y"
        data-automation-id="play-pause-toggle-button"
        (click)="onTogglePlay()"
        [mheTooltip]="((isPlaying$ | async) ? 'readspeaker.pause' : 'readspeaker.play') | translate"
        cdkFocusInitial
        tabindex="0"
      >
        <ng-container [ngSwitch]="isPlaying$ | async">
          <i *ngSwitchCase="true" class="dpg-icon dpg-icon-av-control-pause-outline"></i>
          <i *ngSwitchCase="false" class="dpg-icon dpg-icon-av-control-play-outline"></i>
        </ng-container>
      </button>
      <mat-slider
        #ngSlider
        *ngIf="showPlaybackScrubber"
        class="mhe-a11y rdrx-slider tts-scrubber playback-scrubber"
        data-automation-id="progress-slider-control"
        aria-label="Progress Bar"
      >
        <input
          matSliderThumb
          #ngSliderThumb="matSliderThumb"
          [value]="round(100 / (totalPlaybackTime$ | async) * (playbackPosition$ | async))"
          (change)="onSkip({source: ngSliderThumb, parent: ngSlider, value: ngSliderThumb.value})"
          (input)="onPlaybackScrubberMove()"
          [mheTooltip]="'readspeaker.progress_control' | translate"
          tabindex="0"
        />
      </mat-slider>
      <button
        mhe-button
        [purpose]="ButtonPurpose.Icon"
        class="tts-button mhe-a11y"
        data-automation-id="mute-unmute-toggle-button"
        (click)="onToggleMute()"
        [mheTooltip]="((isMuted$ | async) ? 'readspeaker.unmute' : 'readspeaker.mute') | translate"
        tabindex="0"
        >
        <ng-container [ngSwitch]="isMuted$ | async">
          <i *ngSwitchCase="true" class="dpg-icon dpg-icon-audiooff"></i>
          <i *ngSwitchCase="false" class="dpg-icon dpg-icon-audioon"></i>
        </ng-container>
      </button>
      <mat-slider
        #ngSlider
        class="mhe-a11y rdrx-slider tts-scrubber volume-scrubber"
        data-automation-id="volume-slider-control"
        [max]="100"
        [min]="0"
        [step]="1"
        [displayWith]="calculateValueNow"
        aria-label="Volume Bar"
      >
        <input
          matSliderThumb
          [value]="volume$ | async"
          (change)="onVolumeChange({source: ngSliderThumb, parent: ngSlider, value: ngSliderThumb.value})"
          #ngSliderThumb="matSliderThumb"
          [mheTooltip]="'readspeaker.volume_control' | translate"
          [attr.aria-valuenow]="round((volume$ | async) || 0)"
          tabindex="0"
        />
      </mat-slider>
      <button
        *ngIf="showAutoPageTurn$ | async"
        mhe-button
        [purpose]="ButtonPurpose.Icon"
        class="tts-button mhe-a11y settings-toggle"
        data-automation-id="settings-toggle-button"
        (click)="onViewSettings()"
        [attr.aria-expanded]="showSettingsDropDown$ | async"
        [mheTooltip]="'readspeaker.settings' | translate"
        tabindex="0"
      >
        <i class="dpg-icon dpg-icon-cog"></i>
      </button>
      <button
        *ngIf="(showAutoPageTurn$ | async) === false"
        mhe-button
        [purpose]="ButtonPurpose.Icon"
        class="tts-button mhe-a11y speed-control-toggle"
        data-automation-id="speed-control-toggle-button"
        (click)="onViewSettings()"
        [attr.aria-expanded]="showSettingsDropDown$ | async"
        [mheTooltip]="'readspeaker.view_speed_control' | translate"
        tabindex="0"
      >
        <i class="dpg-icon dpg-icon-dashboard-gauge"></i>
      </button>
    </ng-container>

    <button
      class="readspeaker-close-btn"
      *ngIf="showClose"
      data-automation-id="readspeaker-close-button"
      (click)="onClose()"
      mhe-button
      [purpose]="ButtonPurpose.Icon"
      [mheTooltip]="'readspeaker.close' | translate"
      tabindex="0"
    >
      <i class="dpg-icon dpg-icon-system-close"></i>
    </button>
    </div>
  </div>

  <div *ngIf="showSettingsDropDown$ | async" class="settings-container">
    <div class="control-row">
      <div class="playback-rate-container">
        <label class="tts-speed-label">
          {{ 'readspeaker.speed' | translate }}:
          <!-- The 'tickInterval' was change to 'step' -->
          <mat-slider
            #ngSlider
            class="mhe-a11y rdrx-slider tts-scrubber rate-scrubber"
            data-automation-id="speed-slider-control"
            showTickMarks
            [max]="playbackRateValues[playbackRateValues.length - 1]"
            [min]="playbackRateValues[0]"
            [step]="0.25"
            [displayWith]="formatPlaybackRateAria"
            (input)="onPlaybackRateChange($event)"
            placement="bottom"
            (keydown.escape)="onCloseSettings($event)"
          >
            <input
              [value]="playbackRate$ | async"
              matSliderThumb
              #ngSliderThumb="matSliderThumb"
              [mheTooltip]="'readspeaker.speed_control' | translate"
              placement="top"
              role="slider"
              [attr.aria-label]="'readspeaker.speed' | translate"
              [attr.aria-valuenow]="playbackRate$ | async"
              [attr.aria-valuetext]="(playbackRate$ | async) + 'x'"
              tabindex="0"
            />
          </mat-slider>
        </label>
        <div data-automation-id="playback-speed-rate" class="playback-rate-display">
          {{ playbackRate$ | async }}x
        </div>
      </div>
    </div>
    <div *ngIf="showAutoPageTurn$ | async" class="control-row">
      <div class="auto-page-turn-container">
        <label>
          <mat-checkbox 
            [checked]="effectiveAutoPageTurn$ | async" 
            (keydown.escape)="onCloseSettings($event)"
            (change)="onAutoPageTurnChange($event.checked)"
            data-automation-id="auto-page-turn-checkbox"
          >
          </mat-checkbox>
          {{ 'readspeaker.auto_page_turn' | translate }}
        </label>
      </div>
    </div>
  </div>
</div>

<ng-template #audioErrorMessage>
  <div class="audio-error-alert">
    <span class="sr-only">Error</span>
    Audio failed. Try again later.
  </div>
</ng-template>
